import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/constants';

interface LegalPageSection {
  id: string;
  title: string;
  content: string;
  order: number;
}

interface LegalPage {
  id: number;
  type: 'privacy' | 'cookies' | 'terms' | 'copyright';
  title: string;
  content: string;
  sections?: LegalPageSection[];
  isActive: boolean;
  version: number;
  publishedAt: string;
  updatedAt: string;
  updatedBy?: {
    id: number;
    username: string;
    email: string;
  };
}

interface LegalPageFormData {
  type: 'privacy' | 'cookies' | 'terms' | 'copyright';
  title: string;
  sections: LegalPageSection[];
  isActive: boolean;
}

const ManageLegalPages: React.FC = () => {
  const { token, isAuthenticated, isAdmin } = useAuth();
  const [legalPages, setLegalPages] = useState<LegalPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState<LegalPage | null>(null);
  const [formData, setFormData] = useState<LegalPageFormData>({
    type: 'privacy',
    title: '',
    sections: [],
    isActive: true
  });

  const pageTypeLabels = {
    privacy: 'Politica de Confidențialitate',
    cookies: 'Politica de Cookies',
    terms: 'Termeni și Condiții',
    copyright: 'Protecția Drepturilor de Autor'
  };

  const pageTypeIcons = {
    privacy: '🔒',
    cookies: '🍪',
    terms: '📄',
    copyright: '⚖️'
  };

  // Template-uri pentru secțiuni implicite pentru fiecare tip de pagină
  const defaultSections = {
    privacy: [
      { id: '1', title: 'Informații generale', content: 'Descrierea generală a politicii de confidențialitate...', order: 1 },
      { id: '2', title: 'Tipurile de date personale colectate', content: 'Lista tipurilor de date personale pe care le colectăm...', order: 2 },
      { id: '3', title: 'Scopurile prelucrării datelor', content: 'Scopurile pentru care utilizăm datele personale...', order: 3 },
      { id: '4', title: 'Drepturile utilizatorilor', content: 'Drepturile de care beneficiați conform GDPR...', order: 4 },
      { id: '5', title: 'Securitatea datelor', content: 'Măsurile de securitate pe care le implementăm...', order: 5 },
      { id: '6', title: 'Contact', content: 'Informații de contact pentru întrebări despre confidențialitate...', order: 6 }
    ],
    cookies: [
      { id: '1', title: 'Ce sunt cookie-urile?', content: 'Explicația despre ce sunt cookie-urile și cum funcționează...', order: 1 },
      { id: '2', title: 'Tipurile de cookie-uri folosite', content: 'Lista tipurilor de cookie-uri pe care le folosim...', order: 2 },
      { id: '3', title: 'Gestionarea cookie-urilor', content: 'Cum puteți gestiona sau dezactiva cookie-urile...', order: 3 },
      { id: '4', title: 'Cookie-uri de la terți', content: 'Informații despre cookie-urile de la parteneri...', order: 4 },
      { id: '5', title: 'Contact', content: 'Cum ne puteți contacta pentru întrebări despre cookie-uri...', order: 5 }
    ],
    terms: [
      { id: '1', title: 'Acceptarea termenilor', content: 'Condițiile de acceptare a termenilor de utilizare...', order: 1 },
      { id: '2', title: 'Descrierea serviciului', content: 'Descrierea serviciilor oferite de biblioteca digitală...', order: 2 },
      { id: '3', title: 'Înregistrarea contului', content: 'Condițiile pentru crearea și gestionarea contului...', order: 3 },
      { id: '4', title: 'Utilizarea acceptabilă', content: 'Regulile de utilizare acceptabilă a platformei...', order: 4 },
      { id: '5', title: 'Proprietatea intelectuală', content: 'Informații despre drepturile de proprietate intelectuală...', order: 5 },
      { id: '6', title: 'Limitarea răspunderii', content: 'Limitările răspunderii noastre...', order: 6 },
      { id: '7', title: 'Modificarea termenilor', content: 'Cum și când putem modifica acești termeni...', order: 7 },
      { id: '8', title: 'Contact', content: 'Informații de contact pentru întrebări legale...', order: 8 }
    ],
    copyright: [
      { id: '1', title: 'Angajamentul nostru', content: 'Angajamentul bibliotecii de a respecta drepturile de autor...', order: 1 },
      { id: '2', title: 'Utilizări permise', content: 'Tipurile de utilizări permise pentru materialele din bibliotecă...', order: 2 },
      { id: '3', title: 'Utilizări interzise', content: 'Activitățile interzise privind materialele protejate...', order: 3 },
      { id: '4', title: 'Procedura DMCA', content: 'Procedura pentru raportarea încălcărilor de copyright...', order: 4 },
      { id: '5', title: 'Licențe Creative Commons', content: 'Informații despre materialele cu licențe Creative Commons...', order: 5 },
      { id: '6', title: 'Domeniu public', content: 'Informații despre materialele din domeniul public...', order: 6 },
      { id: '7', title: 'Contact', content: 'Cum să ne contactați pentru întrebări despre copyright...', order: 7 }
    ]
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setError('Nu sunteți autentificat');
      setLoading(false);
      return;
    }

    if (!isAdmin) {
      setError('Nu aveți permisiuni de administrator');
      setLoading(false);
      return;
    }

    fetchLegalPages();
  }, [isAuthenticated, isAdmin]);

  const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
    if (!token) {
      throw new Error('Token de autentificare lipsă');
    }

    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const fetchLegalPages = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`${API_URL}/legal-pages/admin/all`);
      
      if (!response.ok) {
        throw new Error('Eroare la încărcarea paginilor legale');
      }

      const result = await response.json();
      const pagesWithSections = result.data.map((page: LegalPage) => ({
        ...page,
        sections: page.content ? htmlToSections(page.content) : []
      }));
      setLegalPages(pagesWithSections);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Eroare necunoscută');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (!formData.title.trim() || !formData.sections.length) {
        setError('Titlul și cel puțin o secțiune sunt obligatorii');
        return;
      }

      const htmlContent = sectionsToHtml(formData.sections);
      const dataToSend = {
        ...formData,
        content: htmlContent
      };

      const url = editingPage 
        ? `${API_URL}/legal-pages/${editingPage.id}`
        : `${API_URL}/legal-pages`;
      
      const method = editingPage ? 'PUT' : 'POST';
      
      const response = await makeAuthenticatedRequest(url, {
        method,
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Eroare la salvarea paginii');
      }

      await fetchLegalPages();
      setShowModal(false);
      setFormData({
        type: 'privacy',
        title: '',
        sections: [],
        isActive: true
      });
      setEditingPage(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Eroare necunoscută');
    }
  };

  const handleEdit = (page: LegalPage) => {
    setFormData({
      type: page.type,
      title: page.title,
      sections: page.sections || htmlToSections(page.content),
      isActive: page.isActive
    });
    setEditingPage(page);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Sunteți sigur că doriți să ștergeți această pagină?')) {
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`${API_URL}/legal-pages/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchLegalPages();
      } else {
        setError('Eroare la ștergerea paginii');
      }
    } catch (error) {
      console.error('Eroare la ștergerea paginii:', error);
      setError('Eroare la ștergerea paginii');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_URL}/legal-pages/${id}/toggle-status`, {
        method: 'POST'
      });

      if (response.ok) {
        await fetchLegalPages();
      } else {
        setError('Eroare la schimbarea statusului');
      }
    } catch (error) {
      console.error('Eroare la schimbarea statusului:', error);
      setError('Eroare la schimbarea statusului');
    }
  };

  const handleAddNew = () => {
    const existingTypes = legalPages.map(page => page.type);
    const availableTypes = Object.keys(pageTypeLabels).filter(
      type => !existingTypes.includes(type as any)
    );

    if (availableTypes.length === 0) {
      setError('Toate tipurile de pagini legale au fost deja create');
      return;
    }

    const firstType = availableTypes[0] as keyof typeof defaultSections;
    setFormData({
      type: firstType as any,
      title: pageTypeLabels[firstType],
      sections: defaultSections[firstType] || [],
      isActive: true
    });
    setEditingPage(null);
    setShowModal(true);
  };

  const addSection = () => {
    const newSection: LegalPageSection = {
      id: Date.now().toString(),
      title: '',
      content: '',
      order: formData.sections.length + 1
    };
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const updateSection = (id: string, field: keyof LegalPageSection, value: string) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.id === id ? { ...section, [field]: value } : section
      )
    }));
  };

  const removeSection = (id: string) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter(section => section.id !== id)
    }));
  };

  const moveSection = (id: string, direction: 'up' | 'down') => {
    const sections = [...formData.sections];
    const index = sections.findIndex(section => section.id === id);
    
    if (direction === 'up' && index > 0) {
      [sections[index], sections[index - 1]] = [sections[index - 1], sections[index]];
    } else if (direction === 'down' && index < sections.length - 1) {
      [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
    }
    
    // Update order numbers
    sections.forEach((section, idx) => {
      section.order = idx + 1;
    });
    
    setFormData(prev => ({ ...prev, sections }));
  };

  // Helper function to convert sections to HTML for backend compatibility
  const sectionsToHtml = (sections: LegalPageSection[]): string => {
    return sections.map(section => 
      `<section class="content-section">
        <h2>${section.title}</h2>
        <p>${section.content}</p>
      </section>`
    ).join('\n');
  };

  // Helper function to parse HTML content to sections (for existing pages)
  const htmlToSections = (htmlContent: string): LegalPageSection[] => {
    // Simple parser - în practică ar trebui să fie mai robust
    const sections: LegalPageSection[] = [];
    const sectionRegex = /<section[^>]*>[\s\S]*?<\/section>/gi;
    const matches = htmlContent.match(sectionRegex);
    
    if (matches) {
      matches.forEach((match, index) => {
        const titleMatch = match.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/i);
        const contentMatch = match.match(/<p[^>]*>(.*?)<\/p>/i);
        
        sections.push({
          id: (index + 1).toString(),
          title: titleMatch ? titleMatch[1].replace(/<[^>]*>/g, '') : `Secțiunea ${index + 1}`,
          content: contentMatch ? contentMatch[1].replace(/<[^>]*>/g, '') : '',
          order: index + 1
        });
      });
    } else {
      // Fallback - split by paragraphs or headers
      const lines = htmlContent.split('\n').filter(line => line.trim());
      lines.forEach((line, index) => {
        const cleanLine = line.replace(/<[^>]*>/g, '').trim();
        if (cleanLine) {
          sections.push({
            id: (index + 1).toString(),
            title: `Secțiunea ${index + 1}`,
            content: cleanLine,
            order: index + 1
          });
        }
      });
    }
    
    return sections.length > 0 ? sections : [{
      id: '1',
      title: 'Conținut principal',
      content: htmlContent.replace(/<[^>]*>/g, ''),
      order: 1
    }];
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 md:pt-28"></div>
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Se încarcă paginile legale...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Gestionează Paginile Legale</h1>
              <p className="text-gray-600">
                Administrează conținutul paginilor de politici și termeni legali
              </p>
            </div>
            <button
              onClick={handleAddNew}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Adaugă Pagină Nouă
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-800">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Statistici */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Pagini</p>
                <p className="text-2xl font-bold text-gray-800">{legalPages.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Pagini Active</p>
                <p className="text-2xl font-bold text-gray-800">
                  {legalPages.filter(page => page.isActive).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Ultima Actualizare</p>
                <p className="text-sm font-semibold text-gray-800">
                  {legalPages.length > 0 
                    ? new Date(Math.max(...legalPages.map(p => new Date(p.updatedAt).getTime()))).toLocaleDateString('ro-RO')
                    : 'N/A'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Versiuni</p>
                <p className="text-2xl font-bold text-gray-800">
                  {legalPages.reduce((sum, page) => sum + page.version, 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Lista paginilor legale */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Pagini Legale</h2>
          </div>

          {legalPages.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Nicio pagină legală</h3>
              <p className="text-gray-600 mb-6">Nu există încă pagini legale create.</p>
              <button
                onClick={handleAddNew}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Creează Prima Pagină
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {legalPages.map((page) => (
                <div key={page.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-2xl mr-3">{pageTypeIcons[page.type]}</span>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            {page.title}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {pageTypeLabels[page.type]} • Versiunea {page.version}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          page.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {page.isActive ? 'Activ' : 'Inactiv'}
                        </span>
                        <span>
                          Actualizat pe {new Date(page.updatedAt).toLocaleDateString('ro-RO')}
                        </span>
                        {page.updatedBy && (
                          <span>
                            de {page.updatedBy.username}
                          </span>
                        )}
                      </div>

                      <div className="text-gray-600 text-sm">
                        {page.content.length > 200 
                          ? `${page.content.substring(0, 200).replace(/<[^>]*>/g, '')}...`
                          : page.content.replace(/<[^>]*>/g, '')
                        }
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-6">
                      <button
                        onClick={() => handleToggleStatus(page.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                          page.isActive
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {page.isActive ? 'Dezactivează' : 'Activează'}
                      </button>
                      
                      <button
                        onClick={() => handleEdit(page)}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      
                      <button
                        onClick={() => handleDelete(page.id)}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal pentru adăugare/editare */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {editingPage ? 'Editează Pagina Legală' : 'Adaugă Pagină Legală Nouă'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingPage(null);
                  setError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tip Pagină *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        type: e.target.value as any 
                      }))}
                      disabled={!!editingPage}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      required
                    >
                      {Object.entries(pageTypeLabels).map(([key, label]) => (
                        <option key={key} value={key}>
                          {pageTypeIcons[key as keyof typeof pageTypeIcons]} {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="mr-2"
                      />
                      Pagină activă
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titlu Pagină *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      title: e.target.value 
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Introdu titlul paginii..."
                    required
                  />
                </div>

                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Secțiuni conținut
                    </label>
                    <button
                      type="button"
                      onClick={addSection}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Adaugă secțiune
                    </button>
                  </div>

                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {formData.sections.map((section, index) => (
                      <div key={section.id} className="bg-gray-50 p-4 rounded-lg border">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                              Secțiunea {index + 1}
                            </span>
                            <div className="flex space-x-1">
                              <button
                                type="button"
                                onClick={() => moveSection(section.id, 'up')}
                                disabled={index === 0}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                title="Mută în sus"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => moveSection(section.id, 'down')}
                                disabled={index === formData.sections.length - 1}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                title="Mută în jos"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSection(section.id)}
                            className="p-1 text-red-400 hover:text-red-600"
                            title="Șterge secțiunea"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">
                              Titlul secțiunii
                            </label>
                            <input
                              type="text"
                              value={section.title}
                              onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Introduceți titlul secțiunii..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">
                              Conținutul secțiunii
                            </label>
                            <textarea
                              value={section.content}
                              onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                              rows={4}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Introduceți conținutul secțiunii..."
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {formData.sections.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <div className="text-4xl mb-2">📝</div>
                        <p>Nu sunt secțiuni adăugate încă.</p>
                        <p className="text-sm">Apăsați "Adaugă secțiune" pentru a începe.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Previzualizare
                  </label>
                  <div className="bg-gray-50 p-4 rounded-lg border max-h-40 overflow-y-auto">
                    <div className="legal-content">
                      {formData.sections.map((section, index) => (
                        <div key={section.id} className="mb-4 last:mb-0">
                          <h3 className="font-bold text-gray-800 mb-2">
                            {section.title || `Secțiunea ${index + 1}`}
                          </h3>
                          <p className="text-gray-600 text-sm">
                            {section.content || 'Conținut lipsă...'}
                          </p>
                        </div>
                      ))}
                      {formData.sections.length === 0 && (
                        <p className="text-gray-400 italic">Previzualizarea va apărea aici...</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 p-6 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPage(null);
                    setError(null);
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  {editingPage ? 'Actualizează' : 'Creează'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ManageLegalPages; 